import {
  IonInput,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonSpinner,
} from '@ionic/react';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { BugIcon } from '@phosphor-icons/react';
import { close } from 'ionicons/icons';

import { iconProps } from '../../../App';
import { refreshApiBaseUrl } from '../../../axios/Utils/interceptors';
import {
  ApiEnvKey,
  GET_SINIESTROS,
  getApiEnvKey,
  getApiEnvOverride,
  getApiBaseUrl,
  NOTIFICATIONS_PENDING,
  PERSONAL_DATA,
  PETS,
  setApiEnvOverride,
} from '../../../axios/Utils/constants';
import { STORAGE_KEYS } from '../../../hooks/Auth/useAuth';
import { useAuthActions } from '../../../hooks/Auth/useAuth';
import { useAlert } from '../../../hooks/UI/useAlert';
import {
  ActiveTrackingSessionSnapshot,
  TrackingAggregate,
  TrackingRealtimeLog,
  TrackingSessionSummary,
  TrackingTechnicalState,
} from '../../../types/Tracking/types';
import {
  clearPendingDiagnosticsEmail,
  DiagnosticsAccessLevel,
  getPendingDiagnosticsEmail,
  normalizeDiagnosticsEmail,
  setPendingDiagnosticsEmail,
} from '../../../utils/diagnosticsAccess';
import {
  buildActiveSessionSnapshot,
  getTrackingActiveSessionStorageKey,
  getTrackingAggregateStorageKey,
  getTrackingLastSessionStorageKey,
  getTrackingTechnicalSnapshotStorageKey,
  parseStoredTrackingJson,
} from '../../../utils/tracking';
import { getTrackingTechnicalState, subscribeTrackingTechnicalState } from '../../../utils/trackingDiagnostics';
import {
  connectTrackingRealtime,
  disconnectTrackingRealtime,
  emitTrackingRealtimeLog,
  getTrackingRealtimeStatus,
  subscribeTrackingRealtimeLogs,
  subscribeTrackingRealtimeStatus,
  TrackingRealtimeStatus,
} from '../../../utils/trackingRealtime';
import { StorageService } from '../../../utils/storage';
import type { Pet } from '../../../types/Pet/types';
import Alert from '../../UI/Alert';
import Button from '../../UI/Button';
import FormHeader from '../../UI/FormHeader';
import Navigation from '../../UI/Navigation';
import './DiagnosticsPanel.scss';

type DiagnosticsProps = {
  closeModal: () => void;
  userKey?: string;
  accessLevel?: DiagnosticsAccessLevel;
};

type MetricsState = {
  aggregate: TrackingAggregate | null;
  lastSession: TrackingSessionSummary | null;
  activeSession: ActiveTrackingSessionSnapshot | null;
  technicalSnapshot: TrackingTechnicalState | null;
};

type ConsoleFilter = 'all' | 'error' | 'success' | 'api' | 'info';

type EndpointProbeResult = {
  endpoint: string;
  label: string;
  ok: boolean;
  status: number | null;
  durationMs: number;
  detail: string;
  data: unknown | null;
};

type ProbeSummary = {
  capturedAt: string;
  apiEnv: ApiEnvKey;
  baseUrl: string;
  okCount: number;
  failCount: number;
  skippedCount: number;
  results: EndpointProbeResult[];
};

const getApiEnvLabel = (env: ApiEnvKey): string => (env === 'prod' ? 'Producción' : 'Test');

const isValidDiagnosticsEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  if (typeof document === 'undefined') return false;

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    return document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
};

const formatDuration = (valueMs: number | null | undefined): string => {
  if (!valueMs || valueMs <= 0) return '0 min';
  const minutes = Math.max(1, Math.round(valueMs / 60000));
  return `${minutes} min`;
};

const formatProbeDuration = (valueMs: number): string => {
  if (valueMs < 1000) return `${valueMs} ms`;
  if (valueMs < 60000) return `${Math.round(valueMs / 1000)} s`;
  return `${Math.round(valueMs / 60000)} min`;
};

const formatCountEntries = (entries: Record<string, number>, maxItems = 4): string[] => {
  return Object.entries(entries)
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxItems)
    .map(([key, value]) => `${key} (${value})`);
};

const formatTimestamp = (value?: string | null): string => {
  if (!value) return '--:--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--:--';
  return date.toLocaleTimeString();
};

const getAxiosErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const responseMessage = (error.response?.data as { message?: string } | undefined)?.message;
    return responseMessage ?? error.message ?? 'Error desconocido';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Error desconocido';
};

const summarizeProbeData = (data: unknown): string => {
  if (Array.isArray(data)) {
    return `${data.length} registros`;
  }

  if (data && typeof data === 'object') {
    return 'respuesta recibida';
  }

  if (typeof data === 'string') {
    return data.trim() ? data.slice(0, 80) : 'sin contenido';
  }

  return 'sin contenido';
};

const DiagnosticsPanel: React.FC<DiagnosticsProps> = ({
  closeModal,
  userKey,
  accessLevel = 'temporary',
}) => {
  const { alertState, showAlert, hideAlert } = useAlert();
  const { logout } = useAuthActions();

  const [technicalState, setTechnicalState] = useState<TrackingTechnicalState>(
    () => getTrackingTechnicalState(),
  );
  const [metrics, setMetrics] = useState<MetricsState>({
    aggregate: null,
    lastSession: null,
    activeSession: null,
    technicalSnapshot: null,
  });
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [isProbingEndpoints, setIsProbingEndpoints] = useState(false);
  const [lastMetricsRefresh, setLastMetricsRefresh] = useState<string | null>(null);
  const [lastProbeSummary, setLastProbeSummary] = useState<ProbeSummary | null>(null);
  const apiEnv = getApiEnvKey();
  const [realtimeStatus, setRealtimeStatus] = useState<TrackingRealtimeStatus>(
    () => getTrackingRealtimeStatus(),
  );
  const [realtimeLogs, setRealtimeLogs] = useState<TrackingRealtimeLog[]>([]);
  const [localInfoLogs, setLocalInfoLogs] = useState<TrackingRealtimeLog[]>([]);
  const [consoleFilter, setConsoleFilter] = useState<ConsoleFilter>('all');
  const activeSessionId = metrics.activeSession?.sessionId ?? null;
  const apiBaseUrl = getApiBaseUrl();
  const apiEnvLabel = getApiEnvLabel(apiEnv);
  const selectedEnvOverride = getApiEnvOverride();
  const selectedEnvValue = selectedEnvOverride ?? apiEnv;
  const canManageDiagnosticsAccess = accessLevel === 'developer';
  const [diagnosticsEmailInput, setDiagnosticsEmailInput] = useState(
    () => getPendingDiagnosticsEmail() ?? '',
  );
  const [pendingDiagnosticsEmail, setPendingDiagnosticsEmailState] = useState(
    () => getPendingDiagnosticsEmail(),
  );

  useEffect(() => {
    const unsubscribe = subscribeTrackingTechnicalState(setTechnicalState);
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribeStatus = subscribeTrackingRealtimeStatus(setRealtimeStatus);
    const unsubscribeLogs = subscribeTrackingRealtimeLogs((payload) => {
      setRealtimeLogs((prev) => [payload, ...prev].slice(0, 40));
    });

    return () => {
      unsubscribeStatus();
      unsubscribeLogs();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const connect = async () => {
      const token = await StorageService.getSecure(STORAGE_KEYS.token);
      if (!token || !isMounted) return;
      connectTrackingRealtime(token);
    };

    connect();

    return () => {
      isMounted = false;
      disconnectTrackingRealtime();
    };
  }, []);

  const loadMetrics = useCallback(async () => {
    if (!userKey) return;
    const startedAt = Date.now();
    setIsLoadingMetrics(true);

    try {
      const runtimeStatus =
        typeof window !== 'undefined' && window.__fielpetTracking?.status
          ? window.__fielpetTracking.status()
          : null;
      const runtimeActiveSession = runtimeStatus?.activeSession
        ? buildActiveSessionSnapshot(runtimeStatus.activeSession)
        : null;

      if (runtimeStatus?.technicalState) {
        setTechnicalState(runtimeStatus.technicalState);
      } else {
        setTechnicalState(getTrackingTechnicalState());
      }
      const [aggregateRaw, lastSessionRaw, activeSessionRaw, technicalRaw] = await Promise.all([
        StorageService.get(getTrackingAggregateStorageKey(userKey)),
        StorageService.get(getTrackingLastSessionStorageKey(userKey)),
        StorageService.get(getTrackingActiveSessionStorageKey(userKey)),
        StorageService.get(getTrackingTechnicalSnapshotStorageKey(userKey)),
      ]);

      setMetrics({
        aggregate: parseStoredTrackingJson<TrackingAggregate>(aggregateRaw),
        lastSession: parseStoredTrackingJson<TrackingSessionSummary>(lastSessionRaw),
        activeSession:
          runtimeActiveSession ??
          parseStoredTrackingJson<ActiveTrackingSessionSnapshot>(activeSessionRaw),
        technicalSnapshot: parseStoredTrackingJson(technicalRaw),
      });
      setLastMetricsRefresh(new Date().toISOString());
    } finally {
      const elapsedMs = Date.now() - startedAt;
      if (elapsedMs < 500) {
        await new Promise((resolve) => window.setTimeout(resolve, 500 - elapsedMs));
      }
      setIsLoadingMetrics(false);
    }
  }, [userKey]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const pushLocalInfoLog = useCallback((payload: Omit<TrackingRealtimeLog, 'timestamp'>) => {
    const entry: TrackingRealtimeLog = {
      ...payload,
      timestamp: new Date().toISOString(),
    };

    setLocalInfoLogs((prev) => [entry, ...prev].slice(0, 40));
    emitTrackingRealtimeLog(entry);
  }, []);

  const consoleItems = useMemo(() => {
    const errorItems = technicalState.recentErrors.map((error) => ({
      type: 'error',
      title: error.message,
      detail: error.source,
      timestamp: error.timestamp,
    }));
    const apiItems = technicalState.recentApiFailures.map((failure) => ({
      type: 'api',
      title: `${failure.method} ${failure.endpoint}`,
      detail: `${failure.status ?? 'error'}${failure.errorMessage ? ` - ${failure.errorMessage}` : ''}`,
      timestamp: failure.timestamp,
    }));
    const socketItems = realtimeLogs.map((log) => ({
      type: log.type,
      title: log.title,
      detail: log.detail ?? '',
      timestamp: log.timestamp,
    }));
    const infoItems = localInfoLogs.map((log) => ({
      type: log.type,
      title: log.title,
      detail: log.detail ?? '',
      timestamp: log.timestamp,
    }));

    return [...socketItems, ...infoItems, ...errorItems, ...apiItems]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 20);
  }, [technicalState.recentApiFailures, technicalState.recentErrors, realtimeLogs, localInfoLogs]);

  const consoleFilteredItems = useMemo(() => {
    if (consoleFilter === 'all') return consoleItems;
    return consoleItems.filter((item) => item.type === consoleFilter);
  }, [consoleFilter, consoleItems]);

  const consoleFilterCounts = useMemo(() => {
    return consoleItems.reduce(
      (acc, item) => {
        acc[item.type] = (acc[item.type] ?? 0) + 1;
        return acc;
      },
      {
        api: 0,
        error: 0,
        info: 0,
        success: 0,
      } as Record<TrackingRealtimeLog['type'], number>,
    );
  }, [consoleItems]);

  const handleReconnectRealtime = useCallback(async () => {
    const token = await StorageService.getSecure(STORAGE_KEYS.token);
    disconnectTrackingRealtime();
    if (token) {
      connectTrackingRealtime(token);
    }
  }, []);

  const handleClearConsole = useCallback(() => {
    setRealtimeLogs([]);
    setLocalInfoLogs([]);
  }, []);

  const handleResetTechnicalState = useCallback(() => {
    if (typeof window !== 'undefined' && window.__fielpetTrackingDiagnostics?.reset) {
      window.__fielpetTrackingDiagnostics.reset();
      setTechnicalState(getTrackingTechnicalState());
    }
  }, []);

  const supportDiagnosticsPayload = useMemo(() => {
    const runtimeStatus =
      typeof window !== 'undefined' && window.__fielpetTracking?.status
        ? window.__fielpetTracking.status()
        : null;

    return {
      capturedAt: new Date().toISOString(),
      environment: {
        key: apiEnv,
        label: apiEnvLabel,
        baseUrl: apiBaseUrl,
        override: selectedEnvOverride ?? null,
      },
      session: {
        userKey: userKey ?? null,
        activeSessionId,
        activeSession: runtimeStatus?.activeSession ?? metrics.activeSession ?? null,
        lastSession: metrics.lastSession,
        aggregate: metrics.aggregate,
      },
      runtime: {
        realtimeStatus,
        technicalState,
      },
      latestProbe: lastProbeSummary,
      recentConsole: consoleItems.slice(0, 12),
    };
  }, [
    activeSessionId,
    apiBaseUrl,
    apiEnv,
    apiEnvLabel,
    consoleItems,
    lastProbeSummary,
    metrics.activeSession,
    metrics.aggregate,
    metrics.lastSession,
    realtimeStatus,
    selectedEnvOverride,
    technicalState,
    userKey,
  ]);

  const handleCopyDiagnostics = useCallback(async () => {
    const text = JSON.stringify(supportDiagnosticsPayload, null, 2);

    try {
      const copied = await copyTextToClipboard(text);

      if (!copied) {
        throw new Error('No se pudo copiar al portapapeles');
      }

      pushLocalInfoLog({
        type: 'info',
        title: 'Diagnóstico copiado',
        detail: 'JSON copiado al portapapeles',
        sessionId: activeSessionId,
        userKey: userKey ?? null,
        route: technicalState.currentRoute ?? null,
      });

      showAlert({
        header: 'Diagnóstico copiado',
        message: 'Se copió un JSON limpio con el estado del panel.',
        buttons: [{ text: 'Entendido' }],
      });
    } catch (error) {
      console.error('[Diagnostics] Error copiando diagnóstico', error);
      showAlert({
        header: 'No se pudo copiar',
        message: 'Revisá permisos del navegador o intentá de nuevo.',
        buttons: [{ text: 'Entendido' }],
      });
    }
  }, [
    activeSessionId,
    pushLocalInfoLog,
    showAlert,
    supportDiagnosticsPayload,
    technicalState.currentRoute,
    userKey,
  ]);

  const handleProbeEndpoints = useCallback(async () => {
    if (isProbingEndpoints) return;

    setIsProbingEndpoints(true);
    const probeStartedAt = Date.now();

    try {
      const token = await StorageService.getSecure(STORAGE_KEYS.token);

      if (!token) {
        pushLocalInfoLog({
          type: 'error',
          title: 'Chequeo de endpoints',
          detail: 'No hay token de sesión para probar endpoints protegidos',
          sessionId: activeSessionId,
          userKey: userKey ?? null,
          route: technicalState.currentRoute ?? null,
        });

        showAlert({
          header: 'Chequeo de endpoints',
          message: 'No hay token de sesión para probar los endpoints protegidos.',
          buttons: [{ text: 'Entendido' }],
        });
        setLastProbeSummary({
          capturedAt: new Date().toISOString(),
          apiEnv,
          baseUrl: apiBaseUrl,
          okCount: 0,
          failCount: 0,
          skippedCount: 0,
          results: [],
        });
        return;
      }

      const http = axios.create({
        baseURL: apiBaseUrl,
        timeout: 15000,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      let okCount = 0;
      let failCount = 0;
      let skippedCount = 0;
      const probeResults: EndpointProbeResult[] = [];

      const probe = async <T,>(
        label: string,
        endpoint: string,
        request: () => Promise<{ data: T }>,
      ): Promise<EndpointProbeResult> => {
        const startedAt = Date.now();

        try {
          const response = await request();
          const durationMs = Date.now() - startedAt;
          const detail = summarizeProbeData(response.data);
          const result: EndpointProbeResult = {
            endpoint,
            label,
            ok: true,
            status: 200,
            durationMs,
            detail,
            data: response.data,
          };

          okCount += 1;
        pushLocalInfoLog({
          type: 'success',
          title: label,
          detail: `${endpoint} OK - ${detail} · ${formatProbeDuration(durationMs)}`,
          sessionId: activeSessionId,
          userKey: userKey ?? null,
          route: technicalState.currentRoute ?? null,
        });

          probeResults.push(result);
          return result;
        } catch (error) {
          const durationMs = Date.now() - startedAt;
          const status = axios.isAxiosError(error) ? error.response?.status ?? null : null;
          const detail = getAxiosErrorMessage(error);
          const result: EndpointProbeResult = {
            endpoint,
            label,
            ok: false,
            status,
            durationMs,
            detail,
            data: null,
          };

          failCount += 1;
          pushLocalInfoLog({
            type: 'api',
            title: label,
            detail: `${endpoint} ${status ?? 'error'} - ${detail} · ${formatProbeDuration(durationMs)}`,
            sessionId: activeSessionId,
            userKey: userKey ?? null,
            route: technicalState.currentRoute ?? null,
          });

          probeResults.push(result);
          return result;
        }
      };

      pushLocalInfoLog({
        type: 'info',
        title: 'Chequeo de endpoints',
        detail: `Iniciado en ${apiEnvLabel}`,
        sessionId: activeSessionId,
        userKey: userKey ?? null,
        route: technicalState.currentRoute ?? null,
      });

      const profileProbe = await probe('Perfil de usuario', PERSONAL_DATA, () => http.get(PERSONAL_DATA));
      if (!profileProbe.ok && (profileProbe.status === 401 || profileProbe.status === 403)) {
        setLastProbeSummary({
          capturedAt: new Date().toISOString(),
          apiEnv,
          baseUrl: apiBaseUrl,
          okCount,
          failCount,
          skippedCount,
          results: probeResults,
        });
        showAlert({
          header: 'Chequeo de endpoints',
          message: 'El endpoint de perfil devolvio un error de autenticacion. Revisar la sesion.',
          buttons: [{ text: 'Entendido' }],
        });
        return;
      }

      const petsProbe = await probe<Pet[]>('Mascotas', PETS, () => http.get<Pet[]>(PETS));
      const firstPetId =
        petsProbe.ok && Array.isArray(petsProbe.data)
          ? petsProbe.data[0]?.id ?? null
          : null;

      if (firstPetId !== null) {
        await probe('Siniestros de la primera mascota', GET_SINIESTROS, () =>
          http.get(GET_SINIESTROS, { params: { id: firstPetId } }),
        );
      } else {
        skippedCount += 1;
        pushLocalInfoLog({
          type: 'info',
          title: 'Siniestros',
          detail: 'Se omitio porque no hay mascotas para usar como referencia',
          sessionId: activeSessionId,
          userKey: userKey ?? null,
          route: technicalState.currentRoute ?? null,
        });
      }

      await probe('Notificaciones pendientes', NOTIFICATIONS_PENDING, () =>
        http.get(NOTIFICATIONS_PENDING),
      );

      const elapsedMs = Date.now() - probeStartedAt;
      const summary = `OK ${okCount} · Fallos ${failCount} · Omitidos ${skippedCount}`;

      pushLocalInfoLog({
        type: 'info',
        title: 'Chequeo de endpoints',
        detail: `${summary} · Finalizado en ${formatProbeDuration(elapsedMs)}`,
        sessionId: activeSessionId,
        userKey: userKey ?? null,
        route: technicalState.currentRoute ?? null,
      });

      showAlert({
        header: failCount > 0 ? 'Endpoints con fallos' : 'Endpoints OK',
        message: `${summary}\nEntorno: ${apiEnvLabel}\nBase URL: ${apiBaseUrl}`,
        buttons: [{ text: 'Entendido' }],
      });

      setLastProbeSummary({
        capturedAt: new Date().toISOString(),
        apiEnv,
        baseUrl: apiBaseUrl,
        okCount,
        failCount,
        skippedCount,
        results: probeResults,
      });
    } finally {
      setIsProbingEndpoints(false);
    }
  }, [
    apiBaseUrl,
    apiEnv,
    apiEnvLabel,
    isProbingEndpoints,
    activeSessionId,
    pushLocalInfoLog,
    showAlert,
    technicalState.currentRoute,
    userKey,
  ]);

  const applyEnvironmentChange = useCallback(
    async (nextEnv: ApiEnvKey | null, title: string, detail: string) => {
      setApiEnvOverride(nextEnv);
      refreshApiBaseUrl();
      pushLocalInfoLog({
        type: 'info',
        title,
        detail,
        sessionId: activeSessionId,
        userKey: userKey ?? null,
        route: technicalState.currentRoute ?? null,
      });

      await logout({
        skipRemoteLogout: true,
        skipTrackingFlush: true,
      });
    },
    [activeSessionId, logout, pushLocalInfoLog, technicalState.currentRoute, userKey],
  );

  const handleEnvChange = useCallback(
    (nextEnv: ApiEnvKey) => {
      if (nextEnv === selectedEnvValue) return;

      const nextEnvLabel = getApiEnvLabel(nextEnv);

      showAlert({
        header: `Cambiar a ${nextEnvLabel}`,
        message:
          'Vuelve a iniciar sesión!',
        buttons: [
          { text: 'Cancelar', role: 'cancel' },
          {
            text: `Cambiar a ${nextEnvLabel}`,
            role: 'confirm',
            handler: () => {
              void applyEnvironmentChange(
                nextEnv,
                'Entorno seleccionado',
                `Se cambió el entorno a ${nextEnvLabel}.`,
              );
            },
          },
        ],
      });
    },
    [applyEnvironmentChange, selectedEnvValue, showAlert],
  );

  const handleResetEnv = useCallback(() => {
    if (!selectedEnvOverride) return;

    showAlert({
      header: 'Volver al entorno por defecto',
      message:
        'Se quitará el override del panel y se cerrará la sesión local para aplicar el entorno configurado en la app.',
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        {
          text: 'Restablecer',
          role: 'confirm',
          handler: () => {
            void applyEnvironmentChange(
              null,
              'Entorno restablecido',
              'Se volvió a usar el entorno configurado en la app.',
            );
          },
        },
      ],
    });
  }, [applyEnvironmentChange, selectedEnvOverride, showAlert]);

  const handleGrantDiagnosticsAccess = useCallback(() => {
    const normalizedEmail = normalizeDiagnosticsEmail(diagnosticsEmailInput);

    if (!isValidDiagnosticsEmail(normalizedEmail)) {
      showAlert({
        header: 'Correo inválido',
        message: 'Ingresá un correo válido para habilitar el próximo login.',
        buttons: [{ text: 'Entendido' }],
      });
      return;
    }

    const savedEmail = setPendingDiagnosticsEmail(normalizedEmail);
    setPendingDiagnosticsEmailState(savedEmail);
    setDiagnosticsEmailInput(savedEmail);

    showAlert({
      header: 'Diagnóstico habilitado',
      message:
        'Cerrá sesión e ingresá con ese correo. El panel quedará disponible solo durante esa sesión y se quitará al cerrar sesión.',
      buttons: [{ text: 'Entendido' }],
    });
  }, [diagnosticsEmailInput, showAlert]);

  const handleClearPendingDiagnosticsEmail = useCallback(() => {
    clearPendingDiagnosticsEmail();
    setPendingDiagnosticsEmailState(null);
    setDiagnosticsEmailInput('');

    showAlert({
      header: 'Habilitación cancelada',
      message: 'El próximo login ya no tendrá acceso temporal al panel.',
      buttons: [{ text: 'Entendido' }],
    });
  }, [showAlert]);

  const handleConsoleFilterChange = (nextFilter: ConsoleFilter) => {
    setConsoleFilter(nextFilter);
  };

  return (
    <div className="diagnostics-panel">
      <Navigation startIcon={close} onBack={closeModal} />

      <div className="ion-padding-horizontal">
        <FormHeader icon={<BugIcon {...iconProps} />} title="Panel del desarrollador" />
      </div>

      <div className="ion-padding-horizontal ion-padding-bottom diagnostics-panel__content">
        <div className="diagnostics-panel__card diagnostics-panel__card--console">
          <h3 className="diagnostics-panel__title">Consola en vivo</h3>
          <div className="diagnostics-panel__console-header">
            <span className={`diagnostics-panel__status diagnostics-panel__status--${realtimeStatus}`}>
              Socket: {realtimeStatus}
            </span>
            <div className="diagnostics-panel__filters" role="tablist" aria-label="Filtros de consola">
              {(['all', 'error', 'success', 'api', 'info'] as ConsoleFilter[]).map((filter) => {
                const isActive = consoleFilter === filter;
                const labelMap: Record<ConsoleFilter, string> = {
                  all: 'Todo',
                  error: 'Errores',
                  success: 'OK',
                  api: 'API',
                  info: 'Info',
                };
                const count =
                  filter === 'all'
                    ? consoleItems.length
                    : consoleFilterCounts[filter as TrackingRealtimeLog['type']];

                return (
                  <button
                    key={filter}
                    type="button"
                    className={[
                      'diagnostics-panel__filter',
                      isActive && 'diagnostics-panel__filter--active',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => handleConsoleFilterChange(filter)}
                    aria-pressed={isActive}
                  >
                    <span>{labelMap[filter]}</span>
                    <span className="diagnostics-panel__filter-count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="diagnostics-panel__console">
            {consoleFilteredItems.length === 0 ? (
              <div className="diagnostics-panel__console-empty">Sin eventos recientes.</div>
            ) : (
              consoleFilteredItems.map((item, index) => (
                <div className="diagnostics-panel__console-item" key={`${item.type}-${index}`}>
                  <span className={`diagnostics-panel__badge diagnostics-panel__badge--${item.type}`}>
                    {item.type === 'api'
                      ? 'API'
                      : item.type === 'error'
                        ? 'JS'
                        : item.type === 'success'
                          ? 'OK'
                          : 'INFO'}
                  </span>
                  <div className="diagnostics-panel__console-body">
                    <div
                      className={[
                        'diagnostics-panel__console-title',
                        (item.type === 'info' || item.type === 'success') &&
                          'diagnostics-panel__console-title--info',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {item.title}
                    </div>
                    <div
                      className={[
                        'diagnostics-panel__console-detail',
                        (item.type === 'info' || item.type === 'success') &&
                          'diagnostics-panel__console-detail--info',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {item.detail}
                    </div>
                  </div>
                  <span className="diagnostics-panel__console-time">{formatTimestamp(item.timestamp)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="diagnostics-panel__card">
          <h3 className="diagnostics-panel__title">Estado actual</h3>
          <div className="diagnostics-panel__grid">
            <div>
              <span className="diagnostics-panel__label">Ruta actual</span>
              <span className="diagnostics-panel__value">{technicalState.currentRoute ?? '-'}</span>
            </div>
            <div>
              <span className="diagnostics-panel__label">Ruta anterior</span>
              <span className="diagnostics-panel__value">{technicalState.previousRoute ?? '-'}</span>
            </div>
            <div>
              <span className="diagnostics-panel__label">Red</span>
              <span className="diagnostics-panel__value">{technicalState.networkStatus}</span>
            </div>
            <div>
              <span className="diagnostics-panel__label">Última acción OK</span>
              <span className="diagnostics-panel__value">
                {technicalState.lastSuccessfulAction ?? '-'}
              </span>
            </div>
            <div>
              <span className="diagnostics-panel__label">Última acción fallida</span>
              <span className="diagnostics-panel__value">
                {technicalState.lastFailedAction ?? '-'}
              </span>
            </div>
          </div>
        </div>

        <div className="diagnostics-panel__card">
          <div className="diagnostics-panel__row">
            <div className="diagnostics-panel__stack">
              <h3 className="diagnostics-panel__title">Métricas de uso</h3>
              <span className="diagnostics-panel__muted">
                {lastMetricsRefresh ? `Actualizado ${formatTimestamp(lastMetricsRefresh)}` : ' '}
              </span>
            </div>
            <div
              className={[
                'diagnostics-panel__refresh',
                isLoadingMetrics && 'diagnostics-panel__refresh--loading',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <Button size="small" fill="outline" onClick={loadMetrics} disabled={isLoadingMetrics}>
                <span className="diagnostics-panel__refresh-content">
                  {isLoadingMetrics ? <IonSpinner name="crescent" /> : null}
                  <span>{isLoadingMetrics ? 'Actualizando...' : 'Actualizar'}</span>
                </span>
              </Button>
            </div>
          </div>

          <div className="diagnostics-panel__grid">
            <div>
              <span className="diagnostics-panel__label">Uso total</span>
              <span className="diagnostics-panel__value">
                {formatDuration(metrics.aggregate?.totalUsageMs)}
              </span>
            </div>
            <div>
              <span className="diagnostics-panel__label">Sesión en curso</span>
              <span className="diagnostics-panel__value">
                {formatDuration(metrics.activeSession?.sessionUsageMs)}
              </span>
            </div>
            <div>
              <span className="diagnostics-panel__label">Última sesión</span>
              <span className="diagnostics-panel__value">
                {formatDuration(metrics.lastSession?.sessionUsageMs)}
              </span>
            </div>
            <div>
              <span className="diagnostics-panel__label">Pantallas más usadas</span>
              <span className="diagnostics-panel__value">
                {metrics.aggregate?.screenCounts
                  ? formatCountEntries(metrics.aggregate.screenCounts).join(', ')
                  : '-'}
              </span>
            </div>
            <div>
              <span className="diagnostics-panel__label">Flujo más usado</span>
              <span className="diagnostics-panel__value">
                {metrics.aggregate?.mostUsedFlow ?? '-'}
              </span>
            </div>
          </div>
        </div>

        <div className="diagnostics-panel__card">
          <h3 className="diagnostics-panel__title">Opciones de debug</h3>
          <div className="diagnostics-panel__debug-actions">
            <Button
              size="small"
              fill="outline"
              onClick={handleProbeEndpoints}
              noMarginTop
              marginBottom={false}
              disabled={isProbingEndpoints}
            >
              <span className="diagnostics-panel__refresh-content">
                {isProbingEndpoints ? <IonSpinner name="crescent" /> : null}
                <span>{isProbingEndpoints ? 'Probando endpoints...' : 'Probar endpoints'}</span>
              </span>
            </Button>
            <Button
              size="small"
              fill="outline"
              onClick={handleReconnectRealtime}
              noMarginTop
              marginBottom={false}
            >
              Reconectar realtime
            </Button>
            <Button
              size="small"
              fill="outline"
              onClick={handleResetTechnicalState}
              noMarginTop
              marginBottom={false}
            >
              Resetear estado técnico
            </Button>
            <Button
              size="small"
              fill="outline"
              onClick={handleClearConsole}
              noMarginTop
              marginBottom={false}
            >
              Limpiar consola
            </Button>
            <Button
              size="small"
              fill="outline"
              onClick={handleCopyDiagnostics}
              noMarginTop
              marginBottom={false}
            >
              Copiar diagnóstico
            </Button>
          </div>
        </div>

        {canManageDiagnosticsAccess ? (
          <div className="diagnostics-panel__card diagnostics-panel__card--advanced">
            <h3 className="diagnostics-panel__title">Acceso temporal</h3>

            <IonItem>
              <IonLabel position="stacked">Correo a diagnosticar</IonLabel>
              <IonInput
                type="email"
                value={diagnosticsEmailInput}
                placeholder="usuario@email.com"
                onIonInput={(event) => {
                  const nextValue = (event.target as HTMLIonInputElement).value;
                  setDiagnosticsEmailInput(
                    typeof nextValue === 'string' ? nextValue : '',
                  );
                }}
              />
            </IonItem>

            {pendingDiagnosticsEmail ? (
              <span className="diagnostics-panel__muted">
                Próximo login habilitado: {pendingDiagnosticsEmail}
              </span>
            ) : null}

            <div className="diagnostics-panel__debug-actions">
              <Button
                size="small"
                fill="outline"
                onClick={handleGrantDiagnosticsAccess}
                noMarginTop
                marginBottom={false}
              >
                Habilitar próximo login
              </Button>
              {pendingDiagnosticsEmail ? (
                <Button
                  size="small"
                  fill="outline"
                  onClick={handleClearPendingDiagnosticsEmail}
                  noMarginTop
                  marginBottom={false}
                >
                  Cancelar habilitación
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        {canManageDiagnosticsAccess ? (
          <div className="diagnostics-panel__card diagnostics-panel__card--advanced">
            <h3 className="diagnostics-panel__title">Entorno avanzado</h3>

            <IonItem>
              <IonLabel position="stacked">Seleccionar entorno</IonLabel>
              <IonSelect
                value={selectedEnvValue}
                placeholder="Seleccioná un entorno"
                onIonChange={(event) => handleEnvChange(event.detail.value as ApiEnvKey)}
              >
                <IonSelectOption value="test">Test</IonSelectOption>
                <IonSelectOption value="prod">Producción</IonSelectOption>
              </IonSelect>
            </IonItem>

            {selectedEnvOverride ? (
              <Button fill="outline" size="small" onClick={handleResetEnv}>
                Volver al entorno por defecto
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <Alert
        isOpen={alertState.isOpen}
        header={alertState.header}
        message={alertState.message}
        buttons={alertState.buttons}
        onDidDismiss={hideAlert}
      />
    </div>
  );
};

export default DiagnosticsPanel;
